import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PARTNER_TYPES, PILLARS } from "@/lib/content";

const inputCls =
  "w-full bg-transparent border-b border-current/25 py-3 text-base outline-none transition-colors duration-300 focus:border-ptf-accent placeholder:text-current/40";

const EMPTY_FORM = {
  name: "", organisation: "", email: "", phone: "",
  organisation_type: "", area_of_interest: "", message: "",
};

const FieldWrap = ({ label, className = "", children }) => (
  <div className={className}>
    <label className="text-[0.65rem] uppercase tracking-[0.2em] opacity-60">{label}</label>
    {children}
  </div>
);

function SuccessPanel({ onReset }) {
  return (
    <motion.div
      key="done"
      data-testid="partnership-success"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="py-10"
    >
      <span className="grid place-items-center h-14 w-14 rounded-full bg-ptf-accent text-ptf-primary mb-6">
        <Check className="h-7 w-7" />
      </span>
      <h3 className="font-display uppercase text-3xl md:text-4xl tracking-tight">Thank you.</h3>
      <p className="mt-4 max-w-md opacity-80 leading-relaxed">
        Your partnership enquiry has been received. Our team will be in touch as we develop our programmes and collaborations.
      </p>
      <button
        type="button"
        onClick={onReset}
        className="mt-8 text-xs uppercase tracking-[0.18em] underline underline-offset-4 hover:text-ptf-accent"
      >
        Submit another enquiry
      </button>
    </motion.div>
  );
}

export default function PartnershipForm({ dark = false }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/partnership-enquiries", form);
      setDone(true);
      toast.success(data.message || "Enquiry received.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const tone = dark ? "text-ptf-bg" : "text-ptf-text";
  const selectExtra = dark ? "[&>option]:text-ptf-text" : "";

  return (
    <div className={tone}>
      <AnimatePresence mode="wait">
        {done ? (
          <SuccessPanel onReset={() => { setDone(false); setForm(EMPTY_FORM); }} />
        ) : (
          <motion.form
            key="form"
            onSubmit={submit}
            data-testid="partnership-form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7"
          >
            <FieldWrap label="Name *">
              <input required data-testid="pf-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" />
            </FieldWrap>
            <FieldWrap label="Organisation">
              <input data-testid="pf-organisation" value={form.organisation} onChange={set("organisation")} className={inputCls} placeholder="Organisation name" />
            </FieldWrap>
            <FieldWrap label="Email *">
              <input required type="email" data-testid="pf-email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@organisation.org" />
            </FieldWrap>
            <FieldWrap label="Phone">
              <input data-testid="pf-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91" />
            </FieldWrap>
            <FieldWrap label="Organisation Type">
              <select data-testid="pf-type" value={form.organisation_type} onChange={set("organisation_type")} className={`${inputCls} ${selectExtra}`}>
                <option value="">Select type</option>
                {PARTNER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </FieldWrap>
            <FieldWrap label="Area of Interest">
              <select data-testid="pf-area" value={form.area_of_interest} onChange={set("area_of_interest")} className={`${inputCls} ${selectExtra}`}>
                <option value="">Select area</option>
                {PILLARS.map((p) => <option key={p.slug} value={p.title}>{p.title}</option>)}
              </select>
            </FieldWrap>
            <FieldWrap label="Message *" className="md:col-span-2">
              <textarea required data-testid="pf-message" value={form.message} onChange={set("message")} rows={4} className={`${inputCls} resize-none`} placeholder="Tell us about the collaboration you have in mind." />
            </FieldWrap>
            <div className="md:col-span-2 pt-2">
              <button
                type="submit"
                data-testid="pf-submit"
                disabled={loading}
                className={`group inline-flex items-center gap-2 px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] transition-colors duration-300 disabled:opacity-60 ${
                  dark ? "bg-ptf-accent text-ptf-primary hover:brightness-95" : "bg-ptf-primary text-ptf-bg hover:bg-ptf-secondary"
                }`}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {loading ? "Sending" : "Send Enquiry"}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
